// Angular imports
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Import Subscription
import { Subject, filter, map, takeUntil, withLatestFrom } from 'rxjs';

// Import modal service and modals

// Import services
import { LoaderService } from '../loader/loader.service';
import { SideMenuService } from '../services/side-menu.service';
import { MessageService, MessageType } from '../services/message.service';
import { ApplicationContextService } from '../services/application-context.service';

// Import types
import { CalculationMetaApi } from '../types';
import { Store } from '@ngrx/store';
import * as CalculationActions from '../state-management/calculation-actions';
import { selectCalculationMeta } from '../state-management/calculation-selectors';
import { AppState } from '../state-management/app-state';
import { Actions } from '@ngrx/effects';

// Define constants
const MAIN_TAB_INDEX = 0;

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    standalone: false
})
export class MenuComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    private meta: CalculationMetaApi;

    public _activeTab: number = MAIN_TAB_INDEX;

    constructor(private route: ActivatedRoute,
        private router: Router,
        private sideMenuService: SideMenuService,
        private messageService: MessageService,
        private applicationContextService: ApplicationContextService,
        private store: Store<AppState>,
        private actions$: Actions
    ) {
        // Init
        this.init();

        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.meta = meta;
        })
    }

    private async init(): Promise<void> {
        // Handle navigation
        this.route.params.pipe(
            map(params => {
                const { projectId, calcId } = params;

                return { projectId, calcId: calcId };
            }),
            filter(params => !!params.projectId && !!params.calcId),
            withLatestFrom(this.store.select(selectCalculationMeta)),
            takeUntil(this.unsubscribe$),
        ).subscribe(([params, meta]) => {
            const { projectId, calcId } = params;
            // Check if calculation needs to be loaded
            if (meta && meta.id == calcId && meta.projectId == projectId) {
                return;
            }

            // Load the calculation
            this.store.dispatch(CalculationActions.loadCalculation({
                projectId,
                calculationId: calcId
            }))
        });

        // Handle active tab change
        this.applicationContextService.get<number>('activeTabIndex').subscribe(activeTabIndex => {
            this._activeTab = activeTabIndex;
        });
    }

    public ngOnInit() {
        // Close side menu
        this.sideMenuService.close();

        this.actions$.pipe(
            takeUntil(this.unsubscribe$),
            filter(CalculationActions.isErrorAction)
        ).subscribe(errorAction => {
            if (errorAction.error.status == 444) {
                if (!this.meta) {
                    return;
                }

                this.messageService.show({
                    title: 'Fout',
                    message: 'Het lijkt erop dat iemand anders deze calculatie aan het bewerken is. Wil je de calculatie nu unlocken of terug naar het hoofdscherm?',
                    type: MessageType.Error,
                    buttons: [
                        {
                            text: 'Unlocken',
                            handler: async () => {
                                // Unlock calculation
                                this.store.dispatch(CalculationActions.unlockCalculation({
                                    projectId: this.meta.projectId,
                                    calculationId: this.meta.id
                                }));

                                return false;
                            }
                        },
                        {
                            text: 'Terug'
                        }
                    ],
                    closeHandler: () => {
                        // Return to open screen
                        this.router.navigate(['']);

                        // Open side menu
                        this.store.dispatch(CalculationActions.showSideMenu());
                    }
                });
            } else if (!!this.meta) {
                this.messageService.show({
                    title: 'Fout',
                    // message: 'Er lijkt iets fout te zijn gegaan, je wordt terug gestuurd naar het hoofdscherm.',
                    message: `Er is een '${errorAction.type}' fout opgetreden, de calculatie wordt herladen.`,
                    type: MessageType.Error,
                    buttons: [
                        {
                            text: 'Ok'
                        }
                    ],
                    closeHandler: () => {
                        this.store.dispatch(CalculationActions.loadCalculation({
                            projectId: this.meta.projectId,
                            calculationId: this.meta.id,
                            version: this.meta.currentVersion
                        }));
                    }
                });
            } else {
                this.messageService.show({
                    title: 'Fout',
                    // message: 'Er lijkt iets fout te zijn gegaan, je wordt terug gestuurd naar het hoofdscherm.',
                    message: `Er is een '${errorAction.type}' fout opgetreden (${(errorAction.error as Error).message})`,
                    type: MessageType.Error,
                    buttons: [
                        {
                            text: 'Ok'
                        }
                    ],
                })

            }
        })
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /* --- Event handlers --- */

    public _handleTabChange(tabIndex: number) {
        // Set the main tab 
        this._activeTab = tabIndex;
    }
}
